/**
 * Typed HTTP client for arcadia84-api.
 *
 * Reads `VITE_API_BASE_URL` from the build-time env. Local dev defaults
 * to http://localhost:8084/v1 (port matches apps/api/.env). Each
 * fetch attaches a per-session `X-Request-ID` so the API log lines
 * can be correlated back to a cabinet session.
 *
 * Score submission gets a small retry budget (3 attempts with
 * exponential backoff) because the runtime calls it fire-and-forget at
 * round-end — a transient blip shouldn't lose the score, but we also
 * can't block the next round forever. Leaderboard reads are single-shot;
 * the player can re-open the pause menu to retry.
 */

import type {
  CaptureResponse,
  CheckoutResponse,
  ClaimAnonymousScoresRequest,
  ClaimAnonymousScoresResponse,
  DailySeedResponse,
  Leaderboard,
  LeaderboardPeriod,
  PassKind,
  PlayRecord,
  RecordPlayResponse,
  AdEventRecord,
  RunTelemetryRecord,
  ScoreSubmission,
  ScoreSubmissionResponse,
  TasteResult,
  TokenSpendResult,
  Wallet,
} from '@arcraz/arcadia-types';
import { getAccessToken } from '@arcraz/arcadia-auth-web';

const SESSION_REQUEST_ID = newSessionId();
const SUBMIT_RETRIES = 3;
const SUBMIT_BACKOFF_BASE_MS = 250;

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function baseUrl(): string {
  // import.meta.env is Vite-injected at build time; VITE_API_BASE_URL is
  // picked up automatically without a vite.config define block.
  const fromEnv = (import.meta.env?.VITE_API_BASE_URL as string | undefined)?.trim();
  if (fromEnv && fromEnv.length > 0) return fromEnv.replace(/\/+$/, '');
  return 'http://localhost:8084/v1';
}

function url(path: string): string {
  return `${baseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
}

async function headers(extra?: Record<string, string>): Promise<HeadersInit> {
  const base: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Request-ID': SESSION_REQUEST_ID,
    ...(extra ?? {}),
  };
  // Attach Bearer if signed in. getAccessToken auto-refreshes within
  // the 5min expiry window; if refresh fails the session is cleared
  // and we fall through anonymous. No throw — the server's
  // attachPlayerIfPresent middleware tolerates either.
  try {
    const token = await getAccessToken();
    if (token) base.Authorization = `Bearer ${token}`;
  } catch {
    /* anonymous fallback */
  }
  return base;
}

/** A non-2xx response wraps the parsed server error body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseError(resp: Response): Promise<ApiError> {
  let code = 'unknown';
  let message = resp.statusText || `HTTP ${resp.status}`;
  let details: Record<string, unknown> | undefined;
  try {
    const body = (await resp.json()) as {
      error?: { code?: string; message?: string; details?: Record<string, unknown> };
    };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    /* response wasn't JSON; leave defaults */
  }
  return new ApiError(resp.status, code, message, details);
}

/**
 * Submit a final score. Retries on 5xx / network errors; gives up
 * immediately on 4xx (no point retrying a bad payload).
 *
 * Note: server returns `score` as a string in leaderboard responses
 * (BIGINT precision). The submission body sends a JS `number`, which is
 * safe because cartridge scores fit in 53-bit float space.
 */
export async function submitScore(
  submission: ScoreSubmission
): Promise<ScoreSubmissionResponse> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= SUBMIT_RETRIES; attempt += 1) {
    try {
      const resp = await fetch(url('/scores'), {
        method: 'POST',
        headers: await headers(),
        body: JSON.stringify(submission),
      });
      if (resp.ok) {
        return (await resp.json()) as ScoreSubmissionResponse;
      }
      const err = await parseError(resp);
      // 4xx is the client's fault — retrying won't help.
      if (err.status >= 400 && err.status < 500) throw err;
      lastErr = err;
    } catch (err) {
      // Network errors (TypeError from fetch) and 5xx ApiErrors both
      // land here and get retried.
      lastErr = err;
    }
    if (attempt < SUBMIT_RETRIES) {
      const backoff = SUBMIT_BACKOFF_BASE_MS * 2 ** (attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, backoff));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('submitScore failed');
}

export interface GetLeaderboardOptions {
  period?: LeaderboardPeriod;
  limit?: number;
  cartridgeVersion?: number;
}

export async function getLeaderboard(
  cartridgeId: string,
  opts: GetLeaderboardOptions = {}
): Promise<Leaderboard> {
  const params = new URLSearchParams();
  if (opts.period) params.set('period', opts.period);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.cartridgeVersion !== undefined) {
    params.set('cartridgeVersion', String(opts.cartridgeVersion));
  }
  const qs = params.toString();
  const path = `/scores/${encodeURIComponent(cartridgeId)}${qs ? `?${qs}` : ''}`;
  const resp = await fetch(url(path), { method: 'GET', headers: await headers() });
  if (!resp.ok) throw await parseError(resp);
  const body = (await resp.json()) as Leaderboard;
  // Coerce BIGINT-as-string `score` back to numbers for ease of rendering.
  return {
    ...body,
    scores: body.scores.map((entry) => ({
      ...entry,
      score: Number(entry.score),
    })),
  };
}

/**
 * Reassign anonymous scores on this device onto the signed-in player
 * (sc-3026). Caller must be signed in — the API rejects without a
 * Bearer token. Idempotent: subsequent calls return `claimed: 0`.
 *
 * Failures are non-fatal at the call site; the cabinet logs and moves
 * on. Worst case: the player's anonymous scores stay anonymous until
 * a future sign-in.
 */
export async function claimAnonymousScores(
  body: ClaimAnonymousScoresRequest
): Promise<ClaimAnonymousScoresResponse> {
  const resp = await fetch(url('/players/me/claim-anonymous-scores'), {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as ClaimAnonymousScoresResponse;
}

/**
 * Fetch the deterministic daily-challenge seed for a cartridge on the
 * given UTC date. Defaults to today. Throws ApiError on 404 (unknown
 * cartridge) or network failure. Caller handles fallback to a random
 * seed if it wants daily mode to be best-effort instead of blocking.
 */
export async function getDailySeed(
  cartridgeId: string,
  date?: string
): Promise<DailySeedResponse> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  const resp = await fetch(url(`/seeds/${encodeURIComponent(cartridgeId)}${qs}`), {
    method: 'GET',
    headers: await headers(),
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as DailySeedResponse;
}

/**
 * Record one INSERT COIN on the '84 cabinet. Fire-and-forget from the
 * launcher — a lost play is a rounding error, so no retries and errors
 * don't propagate past the caller's `catch`. Version-1 plays were never
 * emitted before this; GET /v1/plays?cartridgeVersion=1 is the classic
 * cabinet's turnstile counter.
 *
 * Credentialed so the server-minted device cookie (a84_did) rides along
 * — the free-play meter and device attribution work on this cabinet the
 * same way they do on the remaster.
 */
export async function recordPlayRemote(play: PlayRecord): Promise<RecordPlayResponse> {
  const resp = await fetch(url('/plays'), {
    method: 'POST',
    headers: await headers(),
    credentials: 'include',
    body: JSON.stringify(play),
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as RecordPlayResponse;
}

/**
 * Fire one run-telemetry record at OUR database (sc-6919). Strictly
 * fire-and-forget — failures are swallowed; telemetry must never
 * disturb a round or a score submission.
 */
export function recordRunTelemetry(record: RunTelemetryRecord): void {
  void (async () => {
    try {
      await fetch(url('/telemetry/runs'), {
        method: 'POST',
        headers: await headers(),
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify(record),
      });
    } catch {
      /* tuning signal lost — never the player's problem */
    }
  })();
}

/**
 * Fire one house-ad event (impression/click) at OUR database (sc-6959).
 * Fire-and-forget, keepalive so a click's navigation doesn't kill it.
 */
export function recordAdEvent(record: AdEventRecord): void {
  void (async () => {
    try {
      await fetch(url('/telemetry/ad'), {
        method: 'POST',
        headers: await headers(),
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify(record),
      });
    } catch {
      /* ad signal lost — never the player's problem */
    }
  })();
}

/**
 * The player's play-access wallet (Monetization v2, sc-6939). Auth'd;
 * throws on non-2xx so the caller can distinguish "unknown" from a real
 * zero — the entry gate treats a throw as fail-closed (show the wall,
 * don't hand out a free run).
 */
export async function getWallet(): Promise<Wallet> {
  const resp = await fetch(url('/wallet'), {
    method: 'GET',
    headers: await headers(),
    credentials: 'include',
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as Wallet;
}

/**
 * Spend one token to start a run (sc-6939). Auth'd; server-authoritative.
 * Throws on non-2xx (incl. the 503 fail-closed path) — the gate blocks
 * the run on any throw. A 200 with {allowed:false} is the exhausted case.
 */
export async function spendToken(): Promise<TokenSpendResult> {
  const resp = await fetch(url('/tokens/spend'), {
    method: 'POST',
    headers: await headers(),
    credentials: 'include',
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as TokenSpendResult;
}

/**
 * Atomically consume the anonymous device's daily free taste (sc-6940).
 * Server-authoritative — the entry gate calls this instead of reading a
 * cached meter, so concurrent launches can't both play free. Throws on
 * non-2xx (incl. 503 fail-closed) — the gate treats a throw as "no taste".
 */
export async function consumeTaste(): Promise<TasteResult> {
  const resp = await fetch(url('/plays/taste'), {
    method: 'POST',
    headers: await headers(),
    credentials: 'include',
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as TasteResult;
}

/**
 * READ-ONLY peek at the anon free-taste count (sc-6943 footer credit). Does
 * NOT consume — the footer shows this to signed-out players. Throws on
 * non-2xx; the footer treats a throw as "hide the indicator".
 */
export async function peekTaste(): Promise<TasteResult> {
  const resp = await fetch(url('/plays/taste'), {
    method: 'GET',
    headers: await headers(),
    credentials: 'include',
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as TasteResult;
}

/**
 * Start a PayPal purchase for a pass (Monetization v2, sc-6943). Auth'd —
 * the server binds the order/subscription to the signed-in player. Returns
 * the PayPal approve URL the cabinet redirects the buyer to.
 */
export async function startCheckout(
  kind: PassKind,
  returnUrl: string,
  cancelUrl: string
): Promise<CheckoutResponse> {
  const resp = await fetch(url('/billing/checkout'), {
    method: 'POST',
    headers: await headers(),
    credentials: 'include',
    body: JSON.stringify({ kind, returnUrl, cancelUrl }),
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as CheckoutResponse;
}

/**
 * Capture an approved day-pass Order after the buyer returns from PayPal
 * (sc-6943). Collects the money; the PAYMENT.CAPTURE.COMPLETED webhook is
 * the source of truth that grants the entitlement. Auth'd.
 */
export async function captureOrder(orderId: string): Promise<CaptureResponse> {
  const resp = await fetch(url('/billing/capture'), {
    method: 'POST',
    headers: await headers(),
    credentials: 'include',
    body: JSON.stringify({ orderId }),
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as CaptureResponse;
}

/** Ask the API for a provider (google) OAuth authorization URL (sc-6940). */
export async function oauthStart(
  provider: string,
  redirectUri: string
): Promise<{ authorizationUrl: string; state: string }> {
  const resp = await fetch(url('/auth/oauth/start'), {
    method: 'POST',
    headers: await headers(),
    credentials: 'include',
    body: JSON.stringify({ provider, redirectUri }),
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as { authorizationUrl: string; state: string };
}
